import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";

/**
 * Quick-create de Producto Comercial.
 * Crea (si no existen) Producto Base + Producto Comercial en un único paso.
 * Al guardar exitosamente devuelve el producto comercial creado via onCreated(p).
 */
export default function QuickCreateProductoDialog({ open, onOpenChange, defaultName = "", onCreated }) {
  const [labs, setLabs] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [pres, setPres] = useState([]);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const empty = {
    // Producto BASE (si crearás uno nuevo)
    crear_base: true,
    producto_base_id: "",
    principio_activo: defaultName || "",
    concentracion: "",
    unidad_concentracion: "",
    forma_farmaceutica: "",
    via_administracion: "",
    // Producto COMERCIAL
    codigo_interno: "",
    nombre_comercial: defaultName || "",
    laboratorio_id: "",
    marca_id: "",
    presentacion_id: "",
    unidades_por_presentacion: 1,
    registro_sanitario: "",
    precio_costo: 0,
    margen: 0.3,
    iva_exento: true,
    iva_porcentaje: 0,
    permite_venta_unidad: true,
    permite_venta_presentacion: true,
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (!open) return;
    setForm({ ...empty, principio_activo: defaultName || "", nombre_comercial: defaultName || "" });
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [l, m, p, b] = await Promise.all([
          api.get("/masters/laboratorios"),
          api.get("/masters/marcas"),
          api.get("/masters/presentaciones"),
          api.get("/masters/productos-base"),
        ]);
        if (!mounted) return;
        setLabs(l.data); setMarcas(m.data); setPres(p.data); setBases(b.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultName]);

  const guardar = async () => {
    if (form.crear_base) {
      if (!form.principio_activo.trim()) return toast.error("Indique el principio activo");
    } else if (!form.producto_base_id) {
      return toast.error("Seleccione un producto base existente");
    }
    if (!form.nombre_comercial.trim()) return toast.error("Indique el nombre comercial");
    if (!form.codigo_interno.trim()) return toast.error("Indique el código interno (SKU)");

    try {
      setSaving(true);
      let baseId = form.producto_base_id;
      if (form.crear_base) {
        const { data: nb } = await api.post("/masters/productos-base", {
          principio_activo: form.principio_activo.trim(),
          concentracion: form.concentracion || null,
          unidad_concentracion: form.unidad_concentracion || null,
          forma_farmaceutica: form.forma_farmaceutica || null,
          via_administracion: form.via_administracion || null,
        });
        baseId = nb.id;
      }

      const payload = {
        producto_base_id: baseId,
        codigo_interno: form.codigo_interno.trim(),
        nombre_comercial: form.nombre_comercial.trim(),
        laboratorio_id: form.laboratorio_id || null,
        marca_id: form.marca_id || null,
        presentacion_id: form.presentacion_id || null,
        unidades_por_presentacion: parseInt(form.unidades_por_presentacion) || 1,
        registro_sanitario: form.registro_sanitario || null,
        precio_costo: parseFloat(form.precio_costo) || 0,
        margen: parseFloat(form.margen) || 0,
        iva_exento: !!form.iva_exento,
        iva_porcentaje: parseFloat(form.iva_porcentaje) || 0,
        permite_venta_unidad: !!form.permite_venta_unidad,
        permite_venta_presentacion: !!form.permite_venta_presentacion,
      };
      const { data: nuevo } = await api.post("/masters/productos-comerciales", payload);
      toast.success(`Producto "${nuevo.nombre_comercial}" creado`);
      onCreated && onCreated(nuevo);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl" data-testid="quick-create-prod-dialog">
        <DialogHeader>
          <DialogTitle><Plus size={18} className="inline mr-2 -mt-1" />Crear nuevo producto comercial</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-10 text-center text-[#5c7368]">Cargando catálogos…</div>
        ) : (
          <div className="space-y-4">
            {/* Producto base */}
            <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">1 · Producto base (familia farmacéutica)</div>
              <div className="flex gap-2 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={form.crear_base} onChange={() => setForm({ ...form, crear_base: true })} data-testid="opt-crear-base" />
                  <span>Crear uno nuevo</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={!form.crear_base} onChange={() => setForm({ ...form, crear_base: false })} data-testid="opt-usar-base" />
                  <span>Usar uno existente</span>
                </label>
              </div>
              {form.crear_base ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Principio activo *</Label>
                    <Input value={form.principio_activo} onChange={(e) => setForm({ ...form, principio_activo: e.target.value })} placeholder="Ej. Paracetamol" data-testid="quick-pa" />
                  </div>
                  <div><Label>Concentración</Label><Input value={form.concentracion} onChange={(e) => setForm({ ...form, concentracion: e.target.value })} placeholder="500" /></div>
                  <div><Label>Unidad</Label><Input value={form.unidad_concentracion} onChange={(e) => setForm({ ...form, unidad_concentracion: e.target.value })} placeholder="mg" /></div>
                  <div><Label>Forma farmacéutica</Label><Input value={form.forma_farmaceutica} onChange={(e) => setForm({ ...form, forma_farmaceutica: e.target.value })} placeholder="Tableta, jarabe…" /></div>
                  <div><Label>Vía administración</Label><Input value={form.via_administracion} onChange={(e) => setForm({ ...form, via_administracion: e.target.value })} placeholder="Oral, IV…" /></div>
                </div>
              ) : (
                <Select value={form.producto_base_id} onValueChange={(v) => setForm({ ...form, producto_base_id: v })}>
                  <SelectTrigger data-testid="quick-base-select"><SelectValue placeholder="Buscar producto base…" /></SelectTrigger>
                  <SelectContent>
                    {bases.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.principio_activo}{b.concentracion ? ` · ${b.concentracion}${b.unidad_concentracion || ""}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Producto comercial */}
            <div className="rounded-md border border-[#d1dbd6] bg-white p-3 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">2 · Producto comercial (variante de venta)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Código interno (SKU) *</Label>
                  <Input value={form.codigo_interno} onChange={(e) => setForm({ ...form, codigo_interno: e.target.value })} placeholder="PC-00999" data-testid="quick-sku" />
                </div>
                <div>
                  <Label>Nombre comercial *</Label>
                  <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} placeholder="Ej. APRONAX 550MG" data-testid="quick-nombre" />
                </div>
                <div>
                  <Label>Laboratorio</Label>
                  <Select value={form.laboratorio_id} onValueChange={(v) => setForm({ ...form, laboratorio_id: v })}>
                    <SelectTrigger data-testid="quick-lab"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{labs.map(l => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marca</Label>
                  <Select value={form.marca_id} onValueChange={(v) => setForm({ ...form, marca_id: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{marcas.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Presentación</Label>
                  <Select value={form.presentacion_id} onValueChange={(v) => setForm({ ...form, presentacion_id: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{pres.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unidades por presentación</Label>
                  <Input type="number" min="1" value={form.unidades_por_presentacion} onChange={(e) => setForm({ ...form, unidades_por_presentacion: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <Label>Registro sanitario</Label>
                  <Input value={form.registro_sanitario} onChange={(e) => setForm({ ...form, registro_sanitario: e.target.value })} placeholder="GBE-1234" />
                </div>
                <div>
                  <Label>Costo unitario</Label>
                  <Input type="number" step="0.01" value={form.precio_costo} onChange={(e) => setForm({ ...form, precio_costo: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Margen (0 a 0.99)</Label>
                  <Input type="number" step="0.01" min="0" max="0.99" value={form.margen} onChange={(e) => setForm({ ...form, margen: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>IVA</Label>
                  <Select value={form.iva_exento ? "exento" : String(form.iva_porcentaje)} onValueChange={(v) => {
                    if (v === "exento") setForm({ ...form, iva_exento: true, iva_porcentaje: 0 });
                    else setForm({ ...form, iva_exento: false, iva_porcentaje: parseFloat(v) });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exento">Exento (medicamentos)</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex gap-4 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.permite_venta_unidad} onCheckedChange={(v) => setForm({ ...form, permite_venta_unidad: !!v })} />
                    <span className="text-sm">Venta por unidad</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.permite_venta_presentacion} onCheckedChange={(v) => setForm({ ...form, permite_venta_presentacion: !!v })} />
                    <span className="text-sm">Venta por presentación (caja)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving || loading} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="quick-save-btn">
            <Save size={14} className="mr-2" />
            {saving ? "Creando…" : "Crear y usar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
